// ─── Tính giá bán — công thức data-driven ──────────────────────────────────
// Phí lấy từ API (admin quản lý). Mỗi phí: %/VND, có/không trần, optional (user bật/tắt).

export type FeeUnit = '%' | 'VND';

/** 1 phí đã resolve giá trị cho shop đang tính. */
export interface FeeForCalc {
  feeKey: string;
  label: string;
  unit: FeeUnit;
  optional: boolean;   // true = chỉ tính khi user bật
  optionalLabel?: string; // nhãn checkbox khi optional
  capped: boolean;     // trần (chỉ với %)
  maxValue: number;    // giá trị trần (VND)
  value: number;       // giá trị của phí cho shop này
}

export interface CalculationInput {
  cogs: number;                       // giá vốn
  productFeePercent: number;          // phí cố định ngành hàng (%)
  shopeeType: 'mall' | 'regular';
  marketingCostPercent?: number;      // chi phí marketing (%)
  desiredProfitPercent: number;       // lợi nhuận mong muốn (%)
  fees: FeeForCalc[];                 // các phí áp dụng cho shop này
  enabledOptional?: Record<string, boolean>; // feeKey → user bật/tắt (cho phí optional)
}

export interface FeeAmount {
  feeKey: string;
  label: string;
  amount: number;
}

export interface CalculationResult {
  productFee: number;      // phí ngành hàng
  marketingCost: number;
  fees: FeeAmount[];       // breakdown từng phí Shopee
  profit: number;
  cogs: number;
  totalCost: number;
  finalPrice: number;
  profitMargin: number;
  isValid: boolean;
  errorMessage?: string;
}

function isActive(fee: FeeForCalc, enabled?: Record<string, boolean>): boolean {
  if (!fee.optional) return true;
  return !!enabled?.[fee.feeKey];
}

function getEmptyResult(): Omit<CalculationResult, 'isValid' | 'errorMessage'> {
  return { productFee: 0, marketingCost: 0, fees: [], profit: 0, cogs: 0, totalCost: 0, finalPrice: 0, profitMargin: 0 };
}

/**
 * Giá bán = (giá vốn + phí VND cố định) / (1 − tổng phí %)
 *  - phí % không trần → cộng vào mẫu số
 *  - phí VND → cộng cố định
 *  - phí % có trần → dùng vòng lặp: chưa chạm trần tính %, chạm trần coi như VND cố định
 *  - phí optional chỉ tính khi user bật
 */
export function calculateSellingPrice(input: CalculationInput): CalculationResult {
  try {
    if (input.cogs <= 0) {
      return { ...getEmptyResult(), isValid: false, errorMessage: 'Giá vốn sản phẩm phải lớn hơn 0' };
    }
    if (input.productFeePercent < 0) {
      return { ...getEmptyResult(), isValid: false, errorMessage: 'Phí cố định sản phẩm không hợp lệ' };
    }
    if (input.desiredProfitPercent < 0) {
      return { ...getEmptyResult(), isValid: false, errorMessage: 'Lợi nhuận mong muốn không hợp lệ' };
    }
    if (input.marketingCostPercent && input.marketingCostPercent < 0) {
      return { ...getEmptyResult(), isValid: false, errorMessage: 'Chi phí marketing không hợp lệ' };
    }

    const active = (input.fees || []).filter((f) => isActive(f, input.enabledOptional));

    // Phí % KHÔNG trần + ngành hàng + lợi nhuận + marketing → mẫu số
    const basePercent =
      input.productFeePercent +
      input.desiredProfitPercent +
      (input.marketingCostPercent || 0) +
      active.filter((f) => f.unit === '%' && !f.capped).reduce((s, f) => s + f.value, 0);

    // Phí VND cố định
    const baseFixed = active.filter((f) => f.unit === 'VND').reduce((s, f) => s + f.value, 0);

    // Phí % CÓ TRẦN
    const cappedFees = active
      .filter((f) => f.unit === '%' && f.capped)
      .map((f) => ({ percent: f.value, max: f.maxValue }));

    // Vòng lặp xử lý trần
    const atCap: boolean[] = cappedFees.map(() => false);
    let sellingPrice = 0;
    let totalPercent = 0;
    for (let iter = 0; iter < 8; iter++) {
      let percentSum = basePercent;
      let fixedSum = baseFixed;
      for (let i = 0; i < cappedFees.length; i++) {
        if (atCap[i]) fixedSum += cappedFees[i].max;
        else percentSum += cappedFees[i].percent;
      }
      totalPercent = percentSum / 100;
      if (totalPercent >= 1) {
        return { ...getEmptyResult(), isValid: false, errorMessage: 'Tổng phí không thể vượt quá 100%' };
      }
      sellingPrice = (input.cogs + fixedSum) / (1 - totalPercent);
      let changed = false;
      for (let i = 0; i < cappedFees.length; i++) {
        const now = cappedFees[i].max > 0 && (sellingPrice * cappedFees[i].percent) / 100 >= cappedFees[i].max;
        if (now !== atCap[i]) { atCap[i] = now; changed = true; }
      }
      if (!changed) break;
    }

    // Breakdown từng phí theo giá bán
    const productFee = sellingPrice * (input.productFeePercent / 100);
    const marketingCost = input.marketingCostPercent ? sellingPrice * (input.marketingCostPercent / 100) : 0;
    const feeAmounts: FeeAmount[] = active.map((f) => {
      let amount = f.unit === 'VND' ? f.value : sellingPrice * (f.value / 100);
      if (f.unit === '%' && f.capped && f.maxValue > 0) amount = Math.min(amount, f.maxValue);
      return { feeKey: f.feeKey, label: f.label, amount };
    });

    const feesTotal = feeAmounts.reduce((s, f) => s + f.amount, 0);
    const totalCost = input.cogs + productFee + marketingCost + feesTotal;
    const actualProfit = sellingPrice - totalCost;
    const profitMargin = sellingPrice ? (actualProfit / sellingPrice) * 100 : 0;

    return {
      productFee,
      marketingCost,
      fees: feeAmounts,
      profit: actualProfit,
      cogs: input.cogs,
      totalCost,
      finalPrice: sellingPrice,
      profitMargin,
      isValid: true,
    };
  } catch (error) {
    return { ...getEmptyResult(), isValid: false, errorMessage: 'Có lỗi xảy ra trong quá trình tính toán' };
  }
}

/** Format số tiền thành chuỗi VND */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Format phần trăm */
export function formatPercentage(value: number): string {
  return `${value.toFixed(2)} %`;
}

/** Parse chuỗi phí thành số */
export function parseFeeString(feeString: string): number {
  const normalizedString = (feeString || '').replace(',', '.');
  const match = normalizedString.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

/** Validate input tiền tệ */
export function validateMoneyInput(value: string): boolean {
  const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
  return !isNaN(numValue) && numValue >= 0;
}

/** Validate input phần trăm */
export function validatePercentageInput(value: string): boolean {
  const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
  return !isNaN(numValue) && numValue >= 0 && numValue <= 100;
}
